import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { caseActionCreators } from 'app/area/case/features/entry-log/case-actions';
import { findOpenEntry, readStoreField } from 'app/area/case/features/entry-log/helpers';
import { findOpenEntryPath } from 'app/area/case/features/entry-log/case-data';
import { api as callsToActionsApi } from 'app/area/case/features/calls-to-actions/api';
import { LabelSpinner } from 'ui/common/label-spinner/label-spinner';
import { tableName } from 'app/area/case/case-data-model';
import { getChildEntities } from 'app/infrastructure/store';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { CompositeKeys, Entity } from 'app/types-shared';
import styles from './entry-log-menu-item.scss';

interface EntryLogMenuItemProps {
  callsToActions: any;
  caseId: number;
  involvedPersonPath?: CompositeKeys;
  translation: {
    textNewEntry?: string;
    textEgress?: string;
    textNewEntrySuccess?: string;
    textEgressSuccess?: string;
  };
  onChanged?: () => void;
  /**
   * The entry the person is inside of. null: the person is not inside. undefined: not known yet,
   * the item is not shown until it is known.
   */
  openEntryPath?: CompositeKeys | null;
  onOpenEntryPathChange?: (openEntryPath: CompositeKeys | null) => void;
}

// The involved person this row belongs to (a row of the involved persons report is one person)
export const getInvolvedPersonPath = (row?: Entity[]) =>
  row?.find(entity => entity.id?.[entity.id.length - 1]?.[tableName.involvedPerson] !== undefined)?.id;

/**
 * The open entry according to the report row. Only reliable when the report has entry columns.
 */
export const getRowOpenEntryPath = (row?: Entity[]): CompositeKeys | null => {
  const involvedPersonPath = getInvolvedPersonPath(row);
  const entries = involvedPersonPath ? getChildEntities(row, involvedPersonPath, tableName.involvedPersonInConfinedSpace) : [];
  return findOpenEntry(entries, readStoreField)?.id ?? null;
};

/**
 * The report row only holds the entries when the report has entry columns, so the case itself is asked
 * whether the person is inside. Falls back to `fallback` when the case could not be read.
 */
export const loadOpenEntryPath = async (
  caseId: number, involvedPersonPath: CompositeKeys, fallback: CompositeKeys | null = null
): Promise<CompositeKeys | null> => {
  try {
    const response = await callsToActionsApi.case(caseId);
    return findOpenEntryPath(response.json.data, involvedPersonPath) ?? null;
  } catch (error) {
    console.error('Failed to read entry log', error);
    return fallback;
  }
};

const createChange = (
  props: EntryLogMenuItemProps, involvedPersonPath: CompositeKeys, openEntryPath: CompositeKeys | null | undefined, userInfo: any
) => {
  const user = userInfo ? { value: userInfo.id, description: userInfo.name } : undefined;
  // The report keeps its own rows, so the case area store does not need the saved case
  return openEntryPath
    ? caseActionCreators.onEgressInvolvedPersonEntry(props.caseId, openEntryPath, user, false)
    : caseActionCreators.onAddInvolvedPersonEntry(props.caseId, involvedPersonPath, user, false);
};

const notifySaved = (translation: EntryLogMenuItemProps['translation'], wasInside: boolean) =>
  toastFactory().dispatchSuccess(wasInside ? translation.textEgressSuccess : translation.textNewEntrySuccess);

/**
 * Saves the change straight to the case and tells the user. Ignores clicks while a change is being saved.
 * Reports the entry the person is inside of after the change, so the item switches between New entry and Egress
 * without depending on the report rows being refreshed (or containing the entries).
 */
const useSaveEntryLogChange = (props: EntryLogMenuItemProps, involvedPersonPath: CompositeKeys) => {
  const dispatch: any = useDispatch();
  const userInfo = useSelector((state: any) => state.app?.userInfo);
  const [isSaving, setIsSaving] = useState(false);
  const { translation, openEntryPath, onChanged, onOpenEntryPathChange } = props;

  const save = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      const result = await dispatch(createChange(props, involvedPersonPath, openEntryPath, userInfo));
      if (result?.saved) {
        onOpenEntryPathChange?.(result.openEntryPath ?? null);
        notifySaved(translation, !!openEntryPath);
      }
      // Also refresh when nothing was saved, the row may be out of date
      onChanged?.();
    } catch (error) {
      console.error('Failed to change entry log', error);
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
};

const isItemAvailable = (props: EntryLogMenuItemProps, involvedPersonPath?: CompositeKeys) =>
  props.callsToActions?.entryLogSupported === true && !!involvedPersonPath && props.openEntryPath !== undefined;

/**
 * Meatball menu item of the involved persons report. Egresses the person when still inside,
 * otherwise adds a new entry. The change is saved straight to the case.
 * Shown once the menu has loaded whether the person is inside, so it never switches label.
 */
export const EntryLogMenuItem = (props: EntryLogMenuItemProps) => {
  const { involvedPersonPath, translation, openEntryPath } = props;
  const { save, isSaving } = useSaveEntryLogChange(props, involvedPersonPath);

  if (!isItemAvailable(props, involvedPersonPath)) {
    return null;
  }

  const text = openEntryPath ? translation.textEgress : translation.textNewEntry;
  return (
    <button type="button" className={`dropdown-item ${isSaving ? styles.entryLogItemSaving : ''}`} disabled={isSaving} onClick={save}>
      {isSaving ? <LabelSpinner /> : text}
    </button>
  );
};
